import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { bumpStaticActivity } from './activityBus';
import { DEFAULT_STATIC_ANALYSIS_MODEL, STATIC_MODEL_OPTIONS } from './staticModels';

export interface AnalyzeRequest {
  analysisModel: string;
}

interface AnalyzeDialogProps {
  open: boolean;
  /** How many ads this will run against — 1 from the detail view, N from a selection. */
  count: number;
  /** True when every target is KNOWN to have been analyzed, so this is a re-run. */
  isRerun?: boolean;
  /** True when some targets' analysis state is unknown (e.g. selected on a
   * page whose data is gone) — warn softly instead of claiming either way. */
  statusUnknown?: boolean;
  /** Kept for call-site compatibility; the dialog no longer fetches anything. */
  service?: unknown;
  onClose: () => void;
  onConfirm: (req: AnalyzeRequest) => void | Promise<void>;
}

/**
 * Confirm an analysis run, and choose the model that runs it.
 *
 * Analysis costs model calls and overwrites the previous read, so it asks
 * first rather than firing on click. One model choice: both locked analysis
 * prompts (1a + 1b) run on it, and the library fields are derived from their
 * output without a further model call.
 */
export default function AnalyzeDialog({
  open, count, isRerun, statusUnknown, onClose, onConfirm,
}: AnalyzeDialogProps) {
  const [analysisModel, setAnalysisModel] = useState(DEFAULT_STATIC_ANALYSIS_MODEL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ analysisModel });
      bumpStaticActivity();
      onClose();
    } catch (err: any) {
      // Stay open with the selection intact: nothing was queued, so closing
      // (or bumping the activity strip) would report a run that never started.
      setError(err?.response?.data?.error || err?.message || 'Failed to queue the analysis');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isRerun ? 'Re-analyze' : 'Analyze'} {count === 1 ? 'this ad' : `${count} ads`}?
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            This reads {count === 1 ? 'the ad' : 'each ad'} with the two locked analysis prompts —
            1a (build record) and 1b (creative teardown) — and fills the library fields from their
            output. It runs on the ad, not on each of its images.
          </Typography>

          {isRerun && (
            <Alert severity="warning">
              {count === 1 ? 'This ad has' : 'These ads have'} already been analyzed. Running again
              replaces the current analysis and extraction.
            </Alert>
          )}

          {!isRerun && statusUnknown && (
            <Alert severity="warning">
              Some selected ads may already be analyzed — running again replaces any existing
              analysis and extraction.
            </Alert>
          )}

          <TextField
            select size="small" label="Model"
            value={analysisModel} onChange={e => setAnalysisModel(e.target.value)}
            helperText="Runs both analysis prompts; the exact prompt text is in the Prompts tab"
          >
            {STATIC_MODEL_OPTIONS.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label} — {m.hint}</MenuItem>
            ))}
          </TextField>

          {/* Only vision models are listed: a static sends one still image, and
              the backend refuses a non-vision model rather than sending a
              request that would arrive with no image attached. */}
          <Typography variant="caption" color="text.disabled">
            Only image-capable models are offered — the video pickers&apos; Gemini and Qwen options
            cannot see a still.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? 'Starting…' : (isRerun ? 'Re-analyze' : 'Analyze')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
